import { currentUser, pointer, pointerId, queryAllClass, queryClass, saveClassRecord } from './parse';
import type { ParseRecord } from './parse';

/* ---------- Learner profile (elearning_Customer) ---------- */

export type CustomerRow = ParseRecord & {
  First_name?: string;
  Last_name?: string;
  Email_id?: string;
  Mob_no?: string;
  UserId?: unknown;
};

function realUserId(): string | undefined {
  const id = pointerId((currentUser() as ParseRecord | null)?.objectId);
  return id && id !== 'local-student' ? id : undefined;
}

export async function loadMyCustomer(): Promise<CustomerRow | undefined> {
  const userId = realUserId();
  if (!userId) return undefined;
  const rows = await queryClass('elearning_Customer', {
    limit: 1,
    keys: ['First_name', 'Last_name', 'Email_id', 'Mob_no', 'UserId'],
    where: (query) => query.equalTo('UserId', pointer('_User', userId)),
  }) as CustomerRow[];
  return rows[0];
}

/** Only First_name/Last_name/Email_id are accepted; the API enforces ownership. */
export function saveMyCustomer(customerId: string, fields: { First_name?: string; Last_name?: string; Email_id?: string }) {
  return saveClassRecord('elearning_Customer', fields, customerId);
}

/* ---------- Doubts & messages (elearning_AskQueries) ---------- */

export type AskQueryRow = ParseRecord & { Subject?: string; Query?: string };

export function submitAskQuery(subject: string, query: string) {
  // The server resolves the customer from the session and forces CustomerPtr.
  return saveClassRecord('elearning_AskQueries', { Subject: subject.trim(), Query: query.trim() });
}

export async function loadMyQueries(limit = 30): Promise<AskQueryRow[]> {
  const me = await loadMyCustomer();
  if (!me?.objectId) return [];
  return queryAllClass('elearning_AskQueries', {
    limit: Math.max(limit, 100),
    order: '-createdAt',
    keys: ['Subject', 'Query', 'CustomerPtr'],
    where: (query) => query.equalTo('CustomerPtr', pointer('elearning_Customer', me.objectId)),
  }) as Promise<AskQueryRow[]>;
}

/* ---------- Static content (FAQ / about / terms) ---------- */

export type FaqRow = ParseRecord & { Question?: string; Answer?: string };

export function loadFaq(): Promise<FaqRow[]> {
  return queryAllClass('elearning_FAQ', { order: 'createdAt' }) as Promise<FaqRow[]>;
}

export async function loadAbout(): Promise<string> {
  const rows = await queryClass('elearning_AboutUs', { limit: 1, keys: ['About'] });
  return typeof rows[0]?.About === 'string' ? rows[0].About : '';
}

export async function loadTerms(): Promise<string> {
  const rows = await queryClass('elearning_TermsAndConditions', { limit: 1, keys: ['Conditions'] });
  return typeof rows[0]?.Conditions === 'string' ? rows[0].Conditions : '';
}
